import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Alert } from '../../components/widgets';
import { DEFAULT_SECTION_ACCESS_TYPE, SECTION_ACCESS_TYPES, SECTIONS, VIEW_ONLY_SECTIONS } from '../../utils/consts';
import { MANAGE_PERMISSIONS_STEPS, translationPrefix } from '../utils';
import css from './SectionsPageStep.css';
import SectionsStepCard from './SectionsStepCard';
import SdkEventStepCard from '../containers/SdkEventStepCard';

// Section that controls SDK events visibility (In-App Events Postback)
const IN_APP_EVENTS_POSTBACK_SECTION = SECTIONS.PARTNER_CONFIGURATION_IN_APP_EVENTS_POSTBACK;

const SectionsPageStep = ({ allSectionPermissions, allSections, onToggleAccessType, cards, step }) => {
    const isAttributionStep = step === MANAGE_PERMISSIONS_STEPS.attribution;
    const allSectionNames = useMemo(() => cards.flatMap(card => card.parts.flatMap(part => part.sectionNames)), [
        cards,
    ]);
    const hasViewAccess = useMemo(
        () =>
            allSectionPermissions.some(
                permission =>
                    !VIEW_ONLY_SECTIONS.includes(permission.name) &&
                    allSectionNames.includes(permission.name) &&
                    permission.access_type === SECTION_ACCESS_TYPES.VIEW
            ),
        [allSectionPermissions, allSectionNames]
    );

    // SDK events selection is only shown when agency has full edit access to In-App Events Postback
    const hasPostbackEditAccess = useMemo(() => {
        if (!isAttributionStep) return false;

        const permission = allSectionPermissions.find(p => p.name === IN_APP_EVENTS_POSTBACK_SECTION);
        const accessType = permission?.access_type ?? DEFAULT_SECTION_ACCESS_TYPE;
        return accessType === SECTION_ACCESS_TYPES.EDIT;
    }, [isAttributionStep, allSectionPermissions]);

    const renderCard = ({ title, description, parts }) => {
        const sectionNames = parts.flatMap(part => part.sectionNames);
        const sections = allSections.filter(section => sectionNames.includes(section.name));
        const sectionPermissions = allSectionPermissions.filter(permission => sectionNames.includes(permission.name));

        return (
            <SectionsStepCard
                key={title}
                title={title}
                description={description}
                sections={sections}
                parts={parts}
                sectionPermissions={sectionPermissions}
                onToggleAccessType={onToggleAccessType}
            />
        );
    };

    return (
        <div className={css.container}>
            {hasViewAccess && (
                <Alert
                    className={css.alert}
                    alertIconClassName={css.alertIcon}
                    type="warning"
                    text={`${translationPrefix}.MANAGE_PERMISSIONS_PAGE.${step.toUpperCase()}.VIEW_ACCESS_DESCRIPTION`}
                />
            )}
            {cards.map(renderCard)}
            {hasPostbackEditAccess && (
                <div className={css.sdkEventsSection}>
                    <SdkEventStepCard />
                </div>
            )}
        </div>
    );
};

SectionsPageStep.propTypes = {
    step: PropTypes.string.isRequired,
    cards: PropTypes.arrayOf(
        PropTypes.shape({
            title: PropTypes.string.isRequired,
            description: PropTypes.string.isRequired,
            parts: PropTypes.arrayOf(
                PropTypes.shape({
                    displayName: PropTypes.string,
                    page: PropTypes.shape({
                        link: PropTypes.string.isRequired,
                        translationKey: PropTypes.string.isRequired,
                    }),
                    sectionNames: PropTypes.arrayOf(PropTypes.string).isRequired,
                    tooltipImageSrc: PropTypes.string,
                    tooltipDescriptionSectionName: PropTypes.string,
                })
            ).isRequired,
        })
    ).isRequired,
    allSectionPermissions: PropTypes.arrayOf(
        PropTypes.shape({
            access_type: PropTypes.number.isRequired,
            display_name: PropTypes.string.isRequired,
            feature_name: PropTypes.string.isRequired,
            name: PropTypes.string.isRequired,
            ui_key: PropTypes.string.isRequired,
        })
    ).isRequired,
    allSections: PropTypes.arrayOf(
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            display_name: PropTypes.string.isRequired,
            feature_name: PropTypes.string.isRequired,
        })
    ).isRequired,
    onToggleAccessType: PropTypes.func.isRequired,
};

export default SectionsPageStep;
